import Phaser from 'phaser';
import { GAME_WIDTH, PlayerCount } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';

export interface PlayerCountSelectorCallbacks {
  onSelectionChanged: (count: PlayerCount) => void;
}

/**
 * Manages the 2/3/4 player count selection buttons.
 */
export class PlayerCountSelector {
  private container: Phaser.GameObjects.Container;
  private buttons: Phaser.GameObjects.Container[] = [];
  private selectedCount: PlayerCount = 4;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: PlayerCountSelectorCallbacks,
    initialCount: PlayerCount = 4
  ) {
    this.selectedCount = initialCount;
    this.container = this.scene.add.container(GAME_WIDTH / 2, 350);
    this.buildSelector();
  }

  private buildSelector(): void {
    // Label
    const label = this.scene.add
      .text(0, -15, 'Players', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    this.container.add(label);

    // Player count buttons (2, 3, 4)
    const counts: PlayerCount[] = [2, 3, 4];
    const buttonWidth = 70;
    const spacing = 20;
    const totalWidth = counts.length * buttonWidth + (counts.length - 1) * spacing;
    const startX = -totalWidth / 2 + buttonWidth / 2;

    counts.forEach((count, index) => {
      const x = startX + index * (buttonWidth + spacing);
      const btn = this.createButton(x, 15, count);
      this.container.add(btn);
      this.buttons.push(btn);
    });

    this.updateSelection();
  }

  private createButton(x: number, y: number, count: PlayerCount): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);

    const isSelected = count === this.selectedCount;
    const bg = this.scene.add
      .rectangle(0, 0, 65, 38, isSelected ? PERSIAN_THEME.colors.gold : PERSIAN_THEME.colors.buttonBg)
      .setStrokeStyle(2, isSelected ? PERSIAN_THEME.colors.bronze : PERSIAN_THEME.colors.bronze);

    const text = this.scene.add
      .text(0, 0, `${count}P`, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '18px',
        color: isSelected ? PERSIAN_THEME.hex.feltGreen : PERSIAN_THEME.hex.textPrimary,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    container.add([bg, text]);
    container.setSize(65, 38);
    container.setInteractive({ useHandCursor: true });
    container.setData('count', count);
    container.setData('bg', bg);
    container.setData('text', text);

    container.on('pointerover', () => {
      if (this.selectedCount !== count) {
        bg.setFillStyle(PERSIAN_THEME.colors.buttonHover);
      }
    });

    container.on('pointerout', () => {
      if (this.selectedCount !== count) {
        bg.setFillStyle(PERSIAN_THEME.colors.buttonBg);
      }
    });

    container.on('pointerdown', () => {
      this.selectedCount = count;
      this.updateSelection();
      this.callbacks.onSelectionChanged(count);
    });

    return container;
  }

  private updateSelection(): void {
    this.buttons.forEach((btn) => {
      const count = btn.getData('count') as PlayerCount;
      const bg = btn.getData('bg') as Phaser.GameObjects.Rectangle;
      const text = btn.getData('text') as Phaser.GameObjects.Text;
      const isSelected = count === this.selectedCount;

      bg.setFillStyle(isSelected ? PERSIAN_THEME.colors.gold : PERSIAN_THEME.colors.buttonBg);
      text.setColor(isSelected ? PERSIAN_THEME.hex.feltGreen : PERSIAN_THEME.hex.textPrimary);
    });
  }

  getSelectedCount(): PlayerCount {
    return this.selectedCount;
  }

  setSelectedCount(count: PlayerCount): void {
    if (count !== this.selectedCount) {
      this.selectedCount = count;
      this.updateSelection();
    }
  }

  show(): void {
    this.container.setVisible(true);
  }

  hide(): void {
    this.container.setVisible(false);
  }

  destroy(): void {
    this.container?.destroy();
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }
}
